/**
 * Shared types for ProfileCard component used in both
 * ProfileConfigurationCenter (preview) and PublicProfileDisplay (full view)
 */

import type { CSSProperties } from 'react';

export interface Achievement {
  id?: number;
  title: string;
  date: string;
  description?: string;
  icon?: string;
  displayOrder?: number;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon?: string;
}

export interface ProfileDisplayData {
  // Identity
  firstName: string;
  lastName: string;
  fullName: string;

  // Professional Info
  jobTitle: string;
  department: string;
  location: string;
  bio: string;

  // Contact
  email: string;
  phone: string;

  // Media
  photo: string | null;
  companyName: string;
  companyLogo: string | null;

  // Content
  skills: string[];
  achievements: Achievement[];
  socialLinks: SocialLink[];

  // Visibility Settings
  showEmail: boolean;
  showPhone: boolean;
  showSkills: boolean;
  showAchievements: boolean;

  // Theming
  colorScheme: string;

  // Profile URL
  profileUrl?: string;
  slug?: string;

  // Branding (optional - from HR settings)
  branding?: {
    primaryColor: string;
    secondaryColor: string;
    disclaimerText?: string;
    footerText?: string;
    showDisclaimer: boolean;
  };
}

export interface ProfileCardProps {
  /** Profile data to display */
  data: ProfileDisplayData;

  /**
   * Display variant:
   * - 'preview': Compact version for configuration center (smaller sizing)
   * - 'full': Full-size version for public profile display
   */
  variant: 'preview' | 'full';

  /** Whether to show QR code section */
  showQRCode?: boolean;

  /** Whether to show action bar (share, download, etc.) */
  showActionBar?: boolean;

  /** Callback when "Leave Contact" is clicked */
  onLeaveContact?: () => void;

  /** Translation function */
  t: (key: string) => string;
}

export interface ThemeColors {
  from: string;
  to: string;
  bg: string;
  text: string;
  badge: string;
  gradient: string;
}

/**
 * Get theme colors based on color scheme name
 */
export const getThemeColors = (colorScheme: string): ThemeColors => {
  const colorMap: Record<string, ThemeColors> = {
    blue: {
      from: 'from-blue-600',
      to: 'to-indigo-600',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      badge: 'bg-blue-100',
      gradient: 'from-blue-500 to-indigo-600'
    },
    green: {
      from: 'from-green-600',
      to: 'to-emerald-600',
      bg: 'bg-green-50',
      text: 'text-green-700',
      badge: 'bg-green-100',
      gradient: 'from-green-500 to-emerald-600'
    },
    purple: {
      from: 'from-purple-600',
      to: 'to-pink-600',
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      badge: 'bg-purple-100',
      gradient: 'from-purple-500 to-pink-600'
    },
    orange: {
      from: 'from-orange-500',
      to: 'to-amber-600',
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      badge: 'bg-orange-100',
      gradient: 'from-orange-500 to-amber-600'
    },
    red: {
      from: 'from-red-600',
      to: 'to-orange-600',
      bg: 'bg-red-50',
      text: 'text-red-700',
      badge: 'bg-red-100',
      gradient: 'from-red-500 to-orange-600'
    },
    gray: {
      from: 'from-gray-500',
      to: 'to-slate-600',
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      badge: 'bg-gray-100',
      gradient: 'from-gray-500 to-slate-600'
    },
    indigo: {
      from: 'from-indigo-600',
      to: 'to-blue-600',
      bg: 'bg-indigo-50',
      text: 'text-indigo-700',
      badge: 'bg-indigo-100',
      gradient: 'from-indigo-500 to-blue-600'
    }
  };
  return colorMap[colorScheme] || colorMap.blue;
};

// CompanyProfileSettings branding defaults (see ProfileCardHeader). When the HR
// branding still matches these, we treat it as "not customized" and let the
// employee's Color Scheme drive the header gradient; any other value is an
// explicit brand override that wins.
const HEADER_BRANDING_DEFAULT_PRIMARY = '#3b82f6';
const HEADER_BRANDING_DEFAULT_SECONDARY = '#6366f1';

/**
 * True when the HR branding has been moved away from the CompanyProfileSettings
 * defaults, i.e. the admin has picked explicit brand colors. When customized,
 * the brand becomes the single accent source for the whole profile (banner,
 * skill chips and page background) so those surfaces coordinate instead of the
 * banner following the brand while the chips/background follow the employee's
 * Color Scheme.
 */
export const isProfileBrandingCustomized = (
  branding?: { primaryColor: string; secondaryColor: string }
): boolean =>
  !!branding && (
    branding.primaryColor?.toLowerCase() !== HEADER_BRANDING_DEFAULT_PRIMARY ||
    branding.secondaryColor?.toLowerCase() !== HEADER_BRANDING_DEFAULT_SECONDARY
  );

// Convert a #rgb / #rrggbb hex to an rgba() string. Falls back to the raw
// value when it isn't a parseable hex (so an already-rgb/hsl brand color still
// renders, just without the requested alpha).
const hexToRgba = (hex: string, alpha: number): string => {
  const value = (hex || '').trim();
  const full = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value);
  if (full) {
    return `rgba(${parseInt(full[1], 16)}, ${parseInt(full[2], 16)}, ${parseInt(full[3], 16)}, ${alpha})`;
  }
  const short = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(value);
  if (short) {
    const r = parseInt(short[1] + short[1], 16);
    const g = parseInt(short[2] + short[2], 16);
    const b = parseInt(short[3] + short[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return value;
};

/**
 * Resolve the header/banner gradient for a profile: either the employee's
 * Color Scheme (Tailwind gradient classes) or an explicit HR brand override
 * (inline linear-gradient style). Shared by ProfileCardHeader (preview) and the
 * full-variant hero so both stay in lockstep.
 */
export const getHeaderGradient = (
  colorScheme: string,
  branding?: { primaryColor: string; secondaryColor: string }
): { className: string; style?: CSSProperties } => {
  if (isProfileBrandingCustomized(branding)) {
    return { className: '', style: { background: `linear-gradient(to right, ${branding!.primaryColor}, ${branding!.secondaryColor})` } };
  }
  const theme = getThemeColors(colorScheme);
  return { className: `bg-gradient-to-r ${theme.from} ${theme.to}` };
};

/**
 * Accent style for a "chip" (e.g. skill pills). Derives from the SAME source as
 * the banner: the HR brand color when customized (soft tint bg + brand text),
 * otherwise the employee Color Scheme's Tailwind classes. Keeps the accent
 * chips coordinated with the hero banner and page background.
 */
export const getAccentChipStyle = (
  colorScheme: string,
  branding?: { primaryColor: string; secondaryColor: string }
): { className: string; style?: CSSProperties } => {
  if (isProfileBrandingCustomized(branding)) {
    const brand = branding!.primaryColor;
    return { className: '', style: { backgroundColor: hexToRgba(brand, 0.12), color: brand } };
  }
  const theme = getThemeColors(colorScheme);
  return { className: `${theme.bg} ${theme.text}` };
};

/**
 * Page background tint for the public profile when HR branding is customized: a
 * soft brand tint fading into the app background, matching the banner/chips.
 * Returns `customized: false` when there's no brand override so the caller can
 * keep its own literal Color-Scheme Tailwind classes (avoids purging
 * dynamically-built class names). Keeps the background coordinated with the
 * banner and skill chips from ONE source.
 */
export const getProfileBackgroundStyle = (
  branding?: { primaryColor: string; secondaryColor: string }
): { customized: boolean; style?: CSSProperties } => {
  if (isProfileBrandingCustomized(branding)) {
    const tint = hexToRgba(branding!.primaryColor, 0.1);
    return { customized: true, style: { backgroundImage: `linear-gradient(to bottom right, ${tint}, transparent 45%)` } };
  }
  return { customized: false };
};

export { getFullUrl as getImageUrl } from '../../utils/url';
